import React, { useState, useCallback, useEffect } from 'react';
import { FileText, BarChart3, Calendar, Users, Building2, Clock, XCircle, HardHat, ChefHat, Loader2, Download, FileDown } from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

type ReportKey =
  | 'daily' | 'monthly' | 'employee' | 'department'
  | 'period' | 'unauthorized' | 'subcontractor' | 'catering';

interface ReportDef {
  key: ReportKey;
  title: string;
  desc: string;
  icon: React.ReactNode;
  reportType?: number; // MealReportType enum for export
}

const REPORTS: ReportDef[] = [
  { key: 'daily', title: 'Günlük Rapor', desc: 'Belirli bir günün özet raporu', icon: <Calendar size={22} />, reportType: 6 },
  { key: 'monthly', title: 'Aylık Rapor', desc: 'Seçili ayın detaylı özeti', icon: <BarChart3 size={22} />, reportType: 7 },
  { key: 'employee', title: 'Personel Bazlı', desc: 'Bir personelin yemek geçmişi', icon: <Users size={22} />, reportType: 4 },
  { key: 'department', title: 'Departman Bazlı', desc: 'Departman yemek istatistikleri', icon: <Building2 size={22} />, reportType: 3 },
  { key: 'period', title: 'Öğün Bazlı', desc: 'Öğün bazlı geçiş trendi', icon: <Clock size={22} />, reportType: 5 },
  { key: 'unauthorized', title: 'İzinsiz Geçişler', desc: 'Reddedilen geçiş kayıtları', icon: <XCircle size={22} />, reportType: 8 },
  { key: 'subcontractor', title: 'Taşeron Günlük', desc: 'Taşeron bazlı sabah/öğlen/akşam sayıları', icon: <HardHat size={22} /> },
  { key: 'catering', title: 'Yemek Firması Raporu', desc: 'Yemek firması bazlı dönem raporu, toplam maliyet', icon: <ChefHat size={22} /> },
];

const todayStr = () => new Date().toISOString().substring(0, 10);
const monthAgoStr = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().substring(0, 10); };

const MealReports: React.FC = () => {
  const [selected, setSelected] = useState<ReportKey>('daily');
  const [start, setStart] = useState(monthAgoStr());
  const [end, setEnd] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  // 2026-08-09: Personel Bazli rapor icin dropdown
  const [employees, setEmployees] = useState<Array<{ id: number; label: string }>>([]);
  const [selectedEmpId, setSelectedEmpId] = useState<number | null>(null);

  useEffect(() => {
    // Personel listesi yukle (Bazli rapor icin)
    (async () => {
      try {
        const r: any = await api.get('/employees?pageSize=1000&isActive=true');
        const raw = r?.data ?? r;
        const arr: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        const opts = arr.map(e => ({
          id: e.id ?? e.employeeId,
          label: `${e.fullName ?? (e.firstName + ' ' + e.lastName)} — #${e.employeeCode ?? e.idNumber ?? '—'}`
        })).filter(o => o.id > 0);
        setEmployees(opts);
        if (opts.length > 0 && !selectedEmpId) setSelectedEmpId(opts[0].id);
      } catch { /* ignore */ }
    })();
  }, []);

  const run = useCallback(async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      let url = '';
      switch (selected) {
        case 'daily': url = `/meals/reports/daily?date=${start}`; break;
        case 'monthly': {
          const d = new Date(start);
          url = `/meals/reports/monthly?year=${d.getFullYear()}&month=${d.getMonth() + 1}`;
          break;
        }
        case 'employee': {
          const empId = selectedEmpId ?? 1;
          url = `/meals/reports/employee/${empId}?start=${start}&end=${end}`;
          break;
        }
        case 'department': url = `/meals/reports/department?start=${start}&end=${end}`; break;
        case 'period': url = `/meals/reports/period?start=${start}&end=${end}`; break;
        case 'unauthorized': url = `/meals/reports/unauthorized?start=${start}&end=${end}`; break;
        case 'subcontractor': url = `/meals/reports/subcontractor-daily?date=${start}`; break;
        case 'catering': url = `/meals/reports/catering-summary?start=${start}&end=${end}`; break;
      }
      const r: any = await api.get(url);
      const raw = r?.data ?? r;
      setResult(raw?.data ?? raw);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Rapor çalıştırılamadı');
    } finally { setLoading(false); }
  }, [selected, start, end, selectedEmpId]);

  const buildExportUrl = (fmt: 'excel' | 'pdf') => {
    const def = REPORTS.find(r => r.key === selected);
    if (!def?.reportType) return null;
    const emp = selected === 'employee' && selectedEmpId ? `&employeeId=${selectedEmpId}` : '';
    return `/api/meals/reports/export?type=${def.reportType}&start=${start}&end=${end}&format=${fmt}${emp}`;
  };

  const exportExcel = () => {
    const url = buildExportUrl('excel');
    if (!url) { uyar('Bu rapor için Excel export desteklenmiyor'); return; }
    window.open(url, '_blank');
  };

  const exportPdf = () => {
    const url = buildExportUrl('pdf');
    if (!url) { uyar('Bu rapor için PDF export desteklenmiyor'); return; }
    window.open(url, '_blank');
  };

  const renderResult = () => {
    if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 size={24} className="animate-spin mx-auto" /></div>;
    if (!result) return <p className="text-center text-slate-400 py-8">Rapor çalıştırın</p>;

    // Try common shapes
    const rows: any[] =
      Array.isArray(result?.rows) ? result.rows :
      Array.isArray(result?.transactions) ? result.transactions :
      Array.isArray(result?.groups) ? result.groups :
      Array.isArray(result) ? result : [];

    if (rows.length === 0) {
      return (
        <div className="py-16 text-center text-slate-400">
          <FileText size={36} className="mx-auto opacity-30 mb-2" /> Kayıt bulunamadı
        </div>
      );
    }

    const cols = Object.keys(rows[0] ?? {}).filter(k => typeof (rows[0] as any)[k] !== 'object' || (rows[0] as any)[k] === null);
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] font-black text-slate-500 uppercase tracking-wider">
              {cols.map(c => <th key={c} className="px-3 py-2">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 300).map((r, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                {cols.map(c => <td key={c} className="px-3 py-2 text-slate-700">{formatVal((r as any)[c])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 300 && <p className="text-xs text-slate-400 py-2 text-center">İlk 300 satır gösteriliyor · Toplam {rows.length}</p>}
      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
          <FileText size={26} className="text-orange-600" /> Yemekhane Raporları
        </h1>
        <p className="text-sm text-slate-500 mt-1">Kişi, departman, öğün bazlı raporlar ve Excel export</p>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {REPORTS.map(r => {
          const sel = r.key === selected;
          return (
            <button key={r.key} onClick={() => setSelected(r.key)}
              className={`text-left p-4 border rounded-2xl transition ${sel ? 'border-orange-500 bg-orange-50 shadow' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
              <div className={`inline-flex w-10 h-10 rounded-xl items-center justify-center mb-2 ${sel ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                {r.icon}
              </div>
              <p className={`font-black text-sm ${sel ? 'text-orange-700' : 'text-slate-800'}`}>{r.title}</p>
              <p className="text-[11px] text-slate-500 mt-1">{r.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
            {selected === 'daily' || selected === 'subcontractor' ? 'Tarih' : 'Başlangıç'}
          </label>
          <input type="date" value={start} onChange={e => setStart(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        {selected !== 'daily' && selected !== 'subcontractor' && (
          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Bitiş</label>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
        )}
        <button onClick={run} disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-60">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />} Raporu Çalıştır
        </button>
        <button onClick={exportExcel}
          className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700">
          <Download size={14} /> Excel
        </button>
        <button onClick={exportPdf}
          className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700">
          <FileDown size={14} /> PDF
        </button>
      </div>

      {/* Personel Bazli rapor icin dropdown */}
      {selected === 'employee' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Personel Seç</label>
          <select
            value={selectedEmpId ?? ''}
            onChange={e => setSelectedEmpId(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full max-w-xl px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none">
            {employees.length === 0 && <option value="">— Personel yükleniyor —</option>}
            {employees.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
          </select>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {renderResult()}
      </div>
    </div>
  );
};

const formatVal = (v: any): string => {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : d.toLocaleString('tr-TR');
    }
    return v;
  }
  return String(v);
};

export default MealReports;
